import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

import CloseIcon from '~/components/MarketplaceIcons/CloseIcon';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

/**
 * Info modal of the DubaiExpress product notices, built to the marketplace
 * mockup: a bottom sheet on mobile, a 560px centered card on desktop. Closes
 * on Escape, scrim click or the "Отлично" button.
 */
const ProductInfoModal = ({ isOpen, onClose, title, children }: Props) => {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    document.body.classList.add('body-no-scroll');

    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.classList.remove('body-no-scroll');
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[1100] flex items-end desktop:items-center justify-center bg-[rgba(20,20,21,0.45)] desktop:p-6 p-0 text-[#141415] antialiased [font-family:var(--fontFamily2)] [text-rendering:auto]"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative desktop:max-h-[86vh] max-h-[88vh] w-full desktop:max-w-[560px] overflow-y-auto desktop:rounded-[20px] rounded-t-[16px] bg-white desktop:px-[30px] px-4 desktop:pt-7 pt-[22px] desktop:pb-6 pb-5"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Закрыть"
          onClick={onClose}
          className="absolute top-[22px] right-[22px] flex size-10 cursor-pointer items-center justify-center rounded-[12px] border border-[#EDEDED] border-solid bg-white text-[#141415] transition-colors duration-[120ms] hover:bg-[#F7F7F7]"
        >
          <CloseIcon className="size-4" />
        </button>
        <h2 className="mt-0! mr-[60px]! mb-[18px]! ml-0! font-semibold text-[24px] leading-[30px] tracking-[-0.02em]">
          {title}
        </h2>
        {children}
        <button
          type="button"
          onClick={onClose}
          className="mt-3.5 h-14 w-full cursor-pointer rounded-[14px] border-0 bg-[#E94E1A] p-0 font-semibold text-[16px] text-white leading-none transition-colors duration-[120ms] [font-family:inherit] hover:bg-[#C43F12]"
        >
          Отлично
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default ProductInfoModal;
